<div class="item-selector-dialog">

    <!-- ═══════════════ Header ═══════════════ -->
    <div class="dialog-header" [ngClass]="config.headerClass">
        <div class="header-content">
            <mat-icon class="header-icon">{{ config.icon }}</mat-icon>
            <div class="header-text">
                <h2>{{ config.title }}</h2>
                <p class="subtitle">{{ config.subtitle }}</p>
            </div>
        </div>
        <button mat-icon-button (click)="cancel()" class="close-btn">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <!-- ═══════════════ Content ═══════════════ -->
    <div class="dialog-content">

        <!-- Search Bar -->
        <div class="search-bar">
            <mat-form-field appearance="outline" class="search-field">
                <mat-icon matPrefix>search</mat-icon>
                <input matInput
                       [(ngModel)]="searchTerm"
                       (ngModelChange)="applySearch()"
                       [placeholder]="config.searchPlaceholder">
                @if (searchTerm) {
                    <button mat-icon-button matSuffix (click)="clearSearch()">
                        <mat-icon>close</mat-icon>
                    </button>
                }
            </mat-form-field>
        </div>

        <!-- ═══════════ TREE VIEW (Positions) ═══════════ -->
        @if (isTreeMode) {
            @if (isPositionsWithHatsMode && positionFreeHats.length > 0) {
                <!-- Tabbed layout: Positions & Hats | Organization Hats -->
                <mat-tab-group class="hat-selector-tabs"
                               [(selectedIndex)]="hatTabIndex"
                               animationDuration="200ms">

                    <!-- ── Tab 1: Positions & Hats (tree) ── -->
                    <mat-tab>
                        <ng-template mat-tab-label>
                            <mat-icon class="tab-icon">account_tree</mat-icon>
                            Positions & Hats
                        </ng-template>

                        <div class="tree-container tab-tree-container">
                            @if (treeDataSource.data.length === 0) {
                                <div class="empty-state">
                                    <mat-icon>account_tree</mat-icon>
                                    <p>{{ config.emptyMessage }}</p>
                                    <span class="empty-sub">{{ config.emptySubmessage }}</span>
                                </div>
                            } @else {
                                <mat-tree [dataSource]="treeDataSource" [treeControl]="treeControl" class="selector-tree">

                                    <!-- Leaf nodes (no children) -->
                                    <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding [matTreeNodePaddingIndent]="24">
                                        <button mat-icon-button disabled class="toggle-spacer"></button>

                                        @if (isNodeSelectable(node)) {
                                            <mat-checkbox
                                                [checked]="node.isSelected"
                                                (change)="toggleTreeNode(node)"
                                                class="node-checkbox">
                                            </mat-checkbox>
                                        }

                                        <mat-icon class="part-icon"
                                                  [ngClass]="node.isHatNode ? 'part-type-hat' : 'part-type-' + node.partType"
                                                  [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                            {{ node.isHatNode ? 'badge' : getPartIcon(node.partType) }}
                                        </mat-icon>

                                        <span class="node-name" [class.selected]="node.isSelected"
                                              [class.hat-node-name]="node.isHatNode"
                                              [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                            {{ node.name }}
                                        </span>

                                        @if (node.isHatNode && node.securityGroupNames?.length > 0) {
                                            <span class="hat-sg-info">
                                                {{ node.securityGroupNames.join(', ') }}
                                            </span>
                                        }

                                        @if (node.isHatNode && !node.isParentSelected) {
                                            <span class="hat-disabled-hint"
                                                  matTooltip="Select the parent org part first to enable hat selection">
                                                <mat-icon>lock</mat-icon>
                                            </span>
                                        }

                                        @if (!node.isHatNode) {
                                            <span class="node-stats">
                                                @if (node.hatCount > 0) {
                                                    <span class="stat">
                                                        <mat-icon class="stat-icon-hats">badge</mat-icon>
                                                        {{ node.hatCount }}
                                                    </span>
                                                }
                                                @if (node.userCount > 0) {
                                                    <span class="stat">
                                                        <mat-icon class="stat-icon-users">person</mat-icon>
                                                        {{ node.userCount }}
                                                    </span>
                                                }
                                            </span>
                                        }

                                        @if (node.isHatNode && node.userCount > 0) {
                                            <span class="node-stats">
                                                <span class="stat">
                                                    <mat-icon class="stat-icon-users">person</mat-icon>
                                                    {{ node.userCount }}
                                                </span>
                                            </span>
                                        }

                                        @if (node.isSelected) {
                                            <mat-icon class="selected-indicator indicator-positions">check_circle</mat-icon>
                                        }
                                    </mat-tree-node>

                                    <!-- Nodes with children -->
                                    <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChildren">
                                        <div class="mat-tree-node" [class.hat-parent-node]="node.isHatNode">
                                            <button mat-icon-button matTreeNodeToggle class="toggle-btn">
                                                <mat-icon>
                                                    {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                                                </mat-icon>
                                            </button>

                                            @if (isNodeSelectable(node)) {
                                                <mat-checkbox
                                                    [checked]="node.isSelected"
                                                    (change)="toggleTreeNode(node)"
                                                    class="node-checkbox">
                                                </mat-checkbox>
                                            }

                                            <mat-icon class="part-icon"
                                                      [ngClass]="node.isHatNode ? 'part-type-hat' : 'part-type-' + node.partType"
                                                      [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                                {{ node.isHatNode ? 'badge' : getPartIcon(node.partType) }}
                                            </mat-icon>

                                            <span class="node-name" [class.selected]="node.isSelected"
                                                  [class.hat-node-name]="node.isHatNode"
                                                  [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                                {{ node.name }}
                                            </span>

                                            @if (node.isHatNode && node.securityGroupNames?.length > 0) {
                                                <span class="hat-sg-info">
                                                    {{ node.securityGroupNames.join(', ') }}
                                                </span>
                                            }

                                            @if (node.isHatNode && !node.isParentSelected) {
                                                <span class="hat-disabled-hint"
                                                      matTooltip="Select the parent org part first to enable hat selection">
                                                    <mat-icon>lock</mat-icon>
                                                </span>
                                            }

                                            @if (!node.isHatNode) {
                                                <span class="node-stats">
                                                    @if (node.hatCount > 0) {
                                                        <span class="stat">
                                                            <mat-icon class="stat-icon-hats">badge</mat-icon>
                                                            {{ node.hatCount }}
                                                        </span>
                                                    }
                                                    @if (node.userCount > 0) {
                                                        <span class="stat">
                                                            <mat-icon class="stat-icon-users">person</mat-icon>
                                                            {{ node.userCount }}
                                                        </span>
                                                    }
                                                </span>
                                            }

                                            @if (node.isSelected) {
                                                <mat-icon class="selected-indicator indicator-positions">check_circle</mat-icon>
                                            }
                                        </div>

                                        <div class="nested-children" [class.hidden]="!treeControl.isExpanded(node)">
                                            <ng-container matTreeNodeOutlet></ng-container>
                                        </div>
                                    </mat-nested-tree-node>
                                </mat-tree>
                            }
                        </div>
                    </mat-tab>

                    <!-- ── Tab 2: Organization Hats (position-free) ── -->
                    <mat-tab>
                        <ng-template mat-tab-label>
                            <mat-icon class="tab-icon">shield</mat-icon>
                            Organization Hats
                            @if (positionFreeHats.length > 0) {
                                <span class="tab-badge">{{ positionFreeHats.length }}</span>
                            }
                        </ng-template>

                        <div class="org-hats-container">
                            <p class="org-hats-hint">
                                These hats are not attached to any position and can be assigned to any user in the organization.
                            </p>

                            <div class="position-free-hats-list">
                                @for (hat of positionFreeHats; track hat.key) {
                                    <div class="free-hat-item" [class.selected]="hat.isSelected"
                                         (click)="togglePositionFreeHat(hat)">
                                        <mat-checkbox
                                            [checked]="hat.isSelected"
                                            (click)="$event.stopPropagation()"
                                            (change)="togglePositionFreeHat(hat)"
                                            class="node-checkbox">
                                        </mat-checkbox>

                                        <mat-icon class="part-icon part-type-hat">badge</mat-icon>

                                        <span class="node-name" [class.selected]="hat.isSelected">
                                            {{ hat.name }}
                                        </span>

                                        @if (hat.securityGroupNames && hat.securityGroupNames.length > 0) {
                                            <span class="hat-sg-info">
                                                {{ hat.securityGroupNames.join(', ') }}
                                            </span>
                                        }

                                        @if (hat.userCount > 0) {
                                            <span class="node-stats">
                                                <span class="stat">
                                                    <mat-icon class="stat-icon-users">person</mat-icon>
                                                    {{ hat.userCount }}
                                                </span>
                                            </span>
                                        }

                                        @if (hat.isSelected) {
                                            <mat-icon class="selected-indicator indicator-positions">check_circle</mat-icon>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </mat-tab>
                </mat-tab-group>
            } @else {
                <!-- Non-tabbed layout (positions only or no position-free hats) -->
                <div class="tree-container">
                    @if (treeDataSource.data.length === 0) {
                        <div class="empty-state">
                            <mat-icon>account_tree</mat-icon>
                            <p>{{ config.emptyMessage }}</p>
                            <span class="empty-sub">{{ config.emptySubmessage }}</span>
                        </div>
                    } @else {
                        <mat-tree [dataSource]="treeDataSource" [treeControl]="treeControl" class="selector-tree">

                            <!-- Leaf nodes (no children) -->
                            <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding [matTreeNodePaddingIndent]="24">
                                <button mat-icon-button disabled class="toggle-spacer"></button>

                                @if (isNodeSelectable(node)) {
                                    <mat-checkbox
                                        [checked]="node.isSelected"
                                        (change)="toggleTreeNode(node)"
                                        class="node-checkbox">
                                    </mat-checkbox>
                                }

                                <mat-icon class="part-icon"
                                          [ngClass]="node.isHatNode ? 'part-type-hat' : 'part-type-' + node.partType"
                                          [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                    {{ node.isHatNode ? 'badge' : getPartIcon(node.partType) }}
                                </mat-icon>

                                <span class="node-name" [class.selected]="node.isSelected"
                                      [class.hat-node-name]="node.isHatNode"
                                      [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                    {{ node.name }}
                                </span>

                                @if (node.isHatNode && node.securityGroupNames?.length > 0) {
                                    <span class="hat-sg-info">
                                        {{ node.securityGroupNames.join(', ') }}
                                    </span>
                                }

                                @if (node.isHatNode && !node.isParentSelected) {
                                    <span class="hat-disabled-hint"
                                          matTooltip="Select the parent org part first to enable hat selection">
                                        <mat-icon>lock</mat-icon>
                                    </span>
                                }

                                @if (!node.isHatNode) {
                                    <span class="node-stats">
                                        @if (node.hatCount > 0) {
                                            <span class="stat">
                                                <mat-icon class="stat-icon-hats">badge</mat-icon>
                                                {{ node.hatCount }}
                                            </span>
                                        }
                                        @if (node.userCount > 0) {
                                            <span class="stat">
                                                <mat-icon class="stat-icon-users">person</mat-icon>
                                                {{ node.userCount }}
                                            </span>
                                        }
                                    </span>
                                }

                                @if (node.isHatNode && node.userCount > 0) {
                                    <span class="node-stats">
                                        <span class="stat">
                                            <mat-icon class="stat-icon-users">person</mat-icon>
                                            {{ node.userCount }}
                                        </span>
                                    </span>
                                }

                                @if (node.isSelected) {
                                    <mat-icon class="selected-indicator indicator-positions">check_circle</mat-icon>
                                }
                            </mat-tree-node>

                            <!-- Nodes with children -->
                            <mat-nested-tree-node *matTreeNodeDef="let node; when: hasChildren">
                                <div class="mat-tree-node" [class.hat-parent-node]="node.isHatNode">
                                    <button mat-icon-button matTreeNodeToggle class="toggle-btn">
                                        <mat-icon>
                                            {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
                                        </mat-icon>
                                    </button>

                                    @if (isNodeSelectable(node)) {
                                        <mat-checkbox
                                            [checked]="node.isSelected"
                                            (change)="toggleTreeNode(node)"
                                            class="node-checkbox">
                                        </mat-checkbox>
                                    }

                                    <mat-icon class="part-icon"
                                              [ngClass]="node.isHatNode ? 'part-type-hat' : 'part-type-' + node.partType"
                                              [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                        {{ node.isHatNode ? 'badge' : getPartIcon(node.partType) }}
                                    </mat-icon>

                                    <span class="node-name" [class.selected]="node.isSelected"
                                          [class.hat-node-name]="node.isHatNode"
                                          [class.disabled-hat]="node.isHatNode && !node.isParentSelected">
                                        {{ node.name }}
                                    </span>

                                    @if (node.isHatNode && node.securityGroupNames?.length > 0) {
                                        <span class="hat-sg-info">
                                            {{ node.securityGroupNames.join(', ') }}
                                        </span>
                                    }

                                    @if (node.isHatNode && !node.isParentSelected) {
                                        <span class="hat-disabled-hint"
                                              matTooltip="Select the parent org part first to enable hat selection">
                                            <mat-icon>lock</mat-icon>
                                        </span>
                                    }

                                    @if (!node.isHatNode) {
                                        <span class="node-stats">
                                            @if (node.hatCount > 0) {
                                                <span class="stat">
                                                    <mat-icon class="stat-icon-hats">badge</mat-icon>
                                                    {{ node.hatCount }}
                                                </span>
                                            }
                                            @if (node.userCount > 0) {
                                                <span class="stat">
                                                    <mat-icon class="stat-icon-users">person</mat-icon>
                                                    {{ node.userCount }}
                                                </span>
                                            }
                                        </span>
                                    }

                                    @if (node.isSelected) {
                                        <mat-icon class="selected-indicator indicator-positions">check_circle</mat-icon>
                                    }
                                </div>

                                <div class="nested-children" [class.hidden]="!treeControl.isExpanded(node)">
                                    <ng-container matTreeNodeOutlet></ng-container>
                                </div>
                            </mat-nested-tree-node>
                        </mat-tree>
                    }
                </div>
            }
        }

        <!-- ═══════════ LIST VIEW (SGs, Policies, Users) ═══════════ -->
        @if (!isTreeMode) {
            <div class="list-container">
                @if (filteredListItems.length === 0) {
                    <div class="empty-state">
                        <mat-icon>{{ config.icon }}</mat-icon>
                        @if (searchTerm) {
                            <p>No results for "{{ searchTerm }}"</p>
                            <span class="empty-sub">Try a different search term</span>
                        } @else {
                            <p>{{ config.emptyMessage }}</p>
                            <span class="empty-sub">{{ config.emptySubmessage }}</span>
                        }
                    </div>
                } @else {
                    @for (item of filteredListItems; track item.key) {
                        <div class="list-item-wrapper" [class.expanded]="item.isExpanded">
                            <div class="list-item" [class.selected]="item.isSelected" (click)="toggleListItem(item)">
                                <mat-checkbox
                                    [checked]="item.isSelected"
                                    (click)="$event.stopPropagation()"
                                    (change)="toggleListItem(item)"
                                    class="item-checkbox">
                                </mat-checkbox>

                                <mat-icon class="item-icon" [ngClass]="'icon-' + data.type">{{ item.icon }}</mat-icon>

                                <div class="item-info">
                                    <span class="item-name"
                                          [matTooltip]="item.description || ''">{{ item.name }}</span>
                                    @if (item.subtitle) {
                                        <span class="item-subtitle">{{ item.subtitle }}</span>
                                    }
                                </div>

                                @if (item.badge) {
                                    <span class="item-badge" [ngStyle]="{ 'background-color': item.badgeColor }">
                                        {{ item.badge }}
                                    </span>
                                }

                                <!-- Right-side actions: tick + expand arrow -->
                                <div class="item-actions-right">
                                    @if (item.isSelected) {
                                        <mat-icon class="selected-indicator" [ngClass]="'indicator-' + data.type">check_circle</mat-icon>
                                    }

                                    @if (item.features && item.features.length > 0) {
                                        <button mat-icon-button class="expand-btn"
                                                (click)="toggleExpand(item, $event)"
                                                [matTooltip]="item.isExpanded ? 'Collapse details' : 'Show features & permissions'">
                                            <mat-icon>{{ item.isExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                                        </button>
                                    }
                                </div>
                            </div>

                            <!-- Expandable feature details panel -->
                            @if (item.isExpanded && item.features) {
                                <div class="feature-details">
                                    @for (feature of item.features; track feature.key) {
                                        <div class="feature-row">
                                            <div class="feature-header">
                                                <mat-icon class="feature-icon" [class.disabled]="!feature.enabled">
                                                    {{ feature.enabled ? 'toggle_on' : 'toggle_off' }}
                                                </mat-icon>
                                                <span class="feature-name" [class.disabled]="!feature.enabled"
                                                      [matTooltip]="feature.description || ''">{{ feature.name }}</span>
                                                @if (feature.permissions.length > 0) {
                                                    <span class="perm-count">{{ feature.permissions.length }} perm{{ feature.permissions.length > 1 ? 's' : '' }}</span>
                                                }
                                            </div>
                                            @if (feature.permissions.length > 0) {
                                                @if (isFeaturesMode) {
                                                    <div class="permissions-actions">
                                                        <button mat-button class="perm-quick-btn" (click)="selectAllPermissions(feature, $event)">
                                                            <mat-icon>select_all</mat-icon>All
                                                        </button>
                                                        <button mat-button class="perm-quick-btn" (click)="clearAllPermissions(feature, $event)">
                                                            <mat-icon>clear_all</mat-icon>None
                                                        </button>
                                                        @if (feature.selectedPermissions && feature.selectedPermissions.length > 0) {
                                                            <span class="perm-selected-count">{{ feature.selectedPermissions.length }} selected</span>
                                                        }
                                                    </div>
                                                }
                                                <div class="permissions-list">
                                                    @for (perm of feature.permissions; track perm) {
                                                        @if (isFeaturesMode) {
                                                            <span class="permission-chip selectable"
                                                                  [class.perm-selected]="feature.selectedPermissions?.includes(perm)"
                                                                  (click)="togglePermission(feature, perm, $event)"
                                                                  [matTooltip]="feature.permissionDescriptions?.[perm] || 'Click to toggle'">
                                                                <mat-icon class="perm-check-icon">{{ feature.selectedPermissions?.includes(perm) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
                                                                {{ perm }}
                                                            </span>
                                                        } @else {
                                                            <span class="permission-chip"
                                                                  [matTooltip]="feature.permissionDescriptions?.[perm] || ''">
                                                                {{ perm }}
                                                            </span>
                                                        }
                                                    }
                                                </div>
                                            }
                                        </div>
                                    }
                                    @if (item.features.length === 0) {
                                        <div class="no-features">No features assigned</div>
                                    }
                                </div>
                            }
                        </div>
                    }
                }
            </div>
        }

    </div>

    <!-- ═══════════════ Summary Footer ═══════════════ -->
    <div class="dialog-summary" [ngClass]="config.headerClass + '-summary'">
        <div class="summary-content">
            <mat-icon>{{ config.icon }}</mat-icon>
            <span class="summary-text">
                <strong>{{ selectedCount }}</strong> of {{ totalCount }} selected
            </span>
        </div>
    </div>

    <!-- ═══════════════ Actions ═══════════════ -->
    <div class="dialog-actions">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" (click)="confirm()">
            <mat-icon>check</mat-icon>
            Confirm Selection
        </button>
    </div>

</div>
